import type { MutableRefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { LoadingOverlay } from '@/components/core';
import type { ReviewBlock, ReviewHighlight } from './adapter/types';
import { FinalizedPdfViewer } from './FinalizedPdfViewer';
import { PdfDocumentLayer } from './PdfDocumentLayer';
import { PreviewStatusChips } from './PreviewStatusChips';
import type { SignatureField } from './SignatureFieldOverlay';
import type { Signatory } from './utils';

type Slot = 'a' | 'b';

interface PdfReviewViewerProps {
  // Page geometry
  scale: number;
  pageWidth: number;
  pageHeight: number;
  pageWidthPt: number;
  pageHeightPt: number;
  templateTitle: string;

  // Which branch to render
  isLoadingPdf: boolean;
  hasPdf: boolean;
  /** Set when the finalized signed PDF should replace the live preview. */
  finalizedPdfUrl: string | null;
  numPages: number | null;
  onFinalizedLoadSuccess: (info: { numPages: number }) => void;

  // Double-buffer wiring
  slotUrls: Record<Slot, string | null>;
  activeSlot: Slot;
  pageRefsA: MutableRefObject<Map<number, HTMLDivElement>>;
  pageRefsB: MutableRefObject<Map<number, HTMLDivElement>>;
  onSlotLoaded: (slot: Slot, numPages: number) => void;
  onSlotReady: (slot: Slot, numPages: number) => void;

  // Status chips
  isEditable: boolean;
  hasUnsavedChanges: boolean;

  // Editing overlays
  hoveredBlock: ReviewHighlight | null;
  activeEditBlock: ReviewHighlight | null;
  flashBlock: ReviewHighlight | null;
  flashNonce: number;
  isEditing: boolean;
  editingBlockId: string | null;
  onPageClick: () => void;
  onTextHover: (e: React.MouseEvent<HTMLDivElement>, page: number) => void;
  onTextLeave: () => void;
  resolveBlockFromTouch: (target: HTMLElement, page: number) => ReviewHighlight | null;
  requestBlockSwitch: (block: ReviewBlock, at: ReviewHighlight | null) => void;

  // Signature fields
  signatureFields: SignatureField[];
  signatories: Signatory[];
  selectedField: string | null;
  onSelectField: (id: string | null) => void;
  onSignatureFieldsChange: (fields: SignatureField[]) => void;
}

/**
 * The `LegalDocument` viewer: a double-buffered react-pdf preview with the
 * editing and signature overlays, plus the read-only branch for an already
 * finalized (signed) PDF.
 *
 * Rendered by the review shell as the document-model-specific surface — the
 * fixed-DOCX flow supplies its own viewer with the same contract, which is why
 * everything here arrives as props rather than being read from a document.
 *
 * Must stay a module-level constant: the shell reconciles on component
 * identity, and a viewer recreated per render would remount the subtree and
 * throw away the buffer's painted canvases on every keystroke.
 */
export function PdfReviewViewer({
  scale,
  pageWidth,
  pageHeight,
  pageWidthPt,
  pageHeightPt,
  templateTitle,
  isLoadingPdf,
  hasPdf,
  finalizedPdfUrl,
  numPages,
  onFinalizedLoadSuccess,
  slotUrls,
  activeSlot,
  pageRefsA,
  pageRefsB,
  onSlotLoaded,
  onSlotReady,
  isEditable,
  hasUnsavedChanges,
  hoveredBlock,
  activeEditBlock,
  flashBlock,
  flashNonce,
  isEditing,
  editingBlockId,
  onPageClick,
  onTextHover,
  onTextLeave,
  resolveBlockFromTouch,
  requestBlockSwitch,
  signatureFields,
  signatories,
  selectedField,
  onSelectField,
  onSignatureFieldsChange,
}: PdfReviewViewerProps) {
  const { t } = useTranslation();

  if (isLoadingPdf) {
    return <LoadingOverlay />;
  }

  if (finalizedPdfUrl) {
    return (
      <FinalizedPdfViewer
        fileUrl={finalizedPdfUrl}
        numPages={numPages}
        scale={scale}
        pageWidth={pageWidth}
        pageHeight={pageHeight}
        onLoadSuccess={onFinalizedLoadSuccess}
      />
    );
  }

  if (!hasPdf) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-globe-grey font-medium">{t('GENERATEREVIEW_PREVIEW_NOPDF')}</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <PreviewStatusChips
        isEditable={isEditable}
        hasUnsavedChanges={hasUnsavedChanges}
        signatureFieldCount={signatureFields.length}
      />

      {/* Double-buffered viewer — the active slot stays fully painted while the
          next blob parses in the hidden buffer slot, then flips in at the same
          scroll position (no loader, no jump). */}
      <div className="relative">
        {(['a', 'b'] as const).map((slot) => {
          const url = slotUrls[slot];
          if (!url) return null;
          const isActiveSlot = slot === activeSlot;
          return (
            <div
              key={slot}
              aria-hidden={!isActiveSlot}
              className={
                isActiveSlot ? '' : 'absolute inset-0 opacity-0 pointer-events-none overflow-hidden'
              }
            >
              <PdfDocumentLayer
                file={url}
                scale={scale}
                pageWidth={pageWidth}
                pageHeight={pageHeight}
                pageWidthPt={pageWidthPt}
                pageHeightPt={pageHeightPt}
                templateTitle={templateTitle}
                pageRefs={slot === 'a' ? pageRefsA : pageRefsB}
                onLoaded={(n) => onSlotLoaded(slot, n)}
                onReady={(n) => onSlotReady(slot, n)}
                interactive={isActiveSlot}
                hoveredBlock={hoveredBlock}
                activeEditBlock={activeEditBlock}
                flashBlock={flashBlock}
                flashNonce={flashNonce}
                isEditing={isEditing}
                editingBlockId={editingBlockId}
                signatureFields={signatureFields}
                signatories={signatories}
                selectedField={selectedField}
                onSelectField={onSelectField}
                onSignatureFieldsChange={onSignatureFieldsChange}
                onPageClick={onPageClick}
                onTextHover={onTextHover}
                onTextLeave={onTextLeave}
                resolveBlockFromTouch={resolveBlockFromTouch}
                requestBlockSwitch={requestBlockSwitch}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}
